import * as React from "react";
import { gql } from "graphql.macro";
import { Currency } from "../../graphql/generated";
import { useMutation } from "../../hooks/useMutation";
import { useScreenState } from "../ScreenState";
import {
  TrackShopOpenedMutation,
  TrackShopOpenedMutationVariables,
} from "./index.generated";

const Shop = React.lazy(() => import("./Shop"));

// Funnel tracking for the agent reports ("% who opened the shop")
const TRACK_SHOP_OPENED_MUTATION = gql`
  mutation trackShopOpened {
    trackShopOpened
  }
`;

const ShopContext = React.createContext<(currency: Currency) => void>(() => {
  throw new Error("Provider not found");
});

export const ShopProvider = ({ children }: { children?: React.ReactNode }) => {
  const [doForceDesktopScreenSize, resetScreenSize] = useScreenState();
  const [currency, setCurrency] = React.useState<Currency>();
  const [, trackShopOpened] = useMutation<
    TrackShopOpenedMutation,
    TrackShopOpenedMutationVariables
  >(TRACK_SHOP_OPENED_MUTATION, false);
  const isOpen = React.useRef(false);
  const close = React.useCallback(() => {
    if (doForceDesktopScreenSize) {
      resetScreenSize();
    }
    isOpen.current = false;
    return setCurrency(undefined);
  }, [doForceDesktopScreenSize, resetScreenSize]);
  const setCurrencyCallback = React.useCallback(
    (currency: Currency) => setCurrency(currency),
    []
  );
  // Switching the currency tab inside the open shop is not a new opening
  const openShop = React.useCallback(
    (currency: Currency) => {
      if (!isOpen.current) {
        isOpen.current = true;
        trackShopOpened({});
      }
      setCurrency(currency);
    },
    [trackShopOpened]
  );
  return (
    <ShopContext.Provider value={openShop}>
      {children}
      {currency !== undefined && (
        <React.Suspense fallback={null}>
          <Shop
            close={close}
            currency={currency}
            setCurrency={setCurrencyCallback}
          />
        </React.Suspense>
      )}
    </ShopContext.Provider>
  );
};

export const useShop = () => {
  return React.useContext(ShopContext);
};
